import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import Script from "next/script";
import type { AnalyticsConsent } from "app-cookies";
import {
	isValidGa4MeasurementId,
	normalizeGa4MeasurementId,
} from "~utils/analytics/ga4-measurement-id";
import {
	CUSTOMER_ANALYTICS_DATA_LAYER,
	sendCustomerAnalyticsCommand,
} from "./customer-analytics-runtime";

interface CustomerAnalyticsContextValue {
	accountSlug?: string;
	isConfigured: boolean;
	measurementId?: string;
}

const CustomerAnalyticsContext = createContext<CustomerAnalyticsContextValue>({
	isConfigured: false,
});

export const useCustomerAnalytics = (): CustomerAnalyticsContextValue =>
	useContext(CustomerAnalyticsContext);

export const CustomerGoogleAnalytics = ({
	accountSlug,
	children,
	consent,
	enabled,
	measurementId: rawMeasurementId,
}: {
	accountSlug?: string;
	children: ReactNode;
	consent: AnalyticsConsent;
	enabled: boolean;
	measurementId?: string | null;
}) => {
	const [isConfigured, setIsConfigured] = useState(false);
	const measurementId = normalizeGa4MeasurementId(rawMeasurementId);
	const shouldMount = Boolean(
		enabled &&
			accountSlug &&
			measurementId &&
			isValidGa4MeasurementId(measurementId),
	);

	useEffect(() => {
		setIsConfigured(false);
		if (!shouldMount || !measurementId) return;

		sendCustomerAnalyticsCommand("js", new Date());
		sendCustomerAnalyticsCommand("consent", "default", {
			ad_storage: "denied",
			analytics_storage: "denied",
			ad_user_data: "denied",
			ad_personalization: "denied",
			wait_for_update: 500,
		});
		sendCustomerAnalyticsCommand("config", measurementId, {
			send_page_view: false,
		});
		setIsConfigured(true);
	}, [accountSlug, measurementId, shouldMount]);

	useEffect(() => {
		if (!isConfigured) return;
		sendCustomerAnalyticsCommand("consent", "update", {
			ad_storage: consent,
			analytics_storage: consent,
			ad_user_data: consent,
			ad_personalization: consent,
		});
	}, [consent, isConfigured]);

	const contextValue = useMemo<CustomerAnalyticsContextValue>(
		() => ({
			accountSlug,
			isConfigured,
			measurementId: shouldMount ? measurementId : undefined,
		}),
		[accountSlug, isConfigured, measurementId, shouldMount],
	);

	return (
		<CustomerAnalyticsContext.Provider value={contextValue}>
			{shouldMount && measurementId ? (
				<Script
					id={`customer-ga-${measurementId}`}
					src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}&l=${CUSTOMER_ANALYTICS_DATA_LAYER}`}
					strategy="afterInteractive"
				/>
			) : null}
			{children}
		</CustomerAnalyticsContext.Provider>
	);
};
